import { Snowflake } from 'lucide-react';
import { compareZones } from '../../../utils/hardinessZone';
import { DISCLAIMER_TEXTS } from '../../../constants/disclaimers';

const HARDINESS_DISCLAIMER = DISCLAIMER_TEXTS.hardiness.sourceText;

/**
 * Non-blocking warning badge for plants whose minimum hardiness zone
 * exceeds the garden's zone. Renders nothing when the plant is compatible
 * ('ok') or when zone data is missing/unparseable ('unknown').
 *
 * @param {string} plantZone  - Plant's minimum required zone, e.g. "7b"
 * @param {string} gardenZone - Garden's USDA zone from wizard, e.g. "Zone 8a"
 * @param {'compact'|'full'} [variant='compact'] - Display variant:
 *   compact = small inline badge for PlantCard list view
 *   full    = info box for PlantDetailDialog section
 */
const HardinessWarningBadge = ({ plantZone, gardenZone, variant = 'compact' }) => {
  if (!plantZone || !gardenZone) return null;

  const result = compareZones(plantZone, gardenZone);
  if (result === 'ok' || result === 'unknown') return null;

  const isMarginal = result === 'marginal';

  const colors = isMarginal
    ? {
        bg: 'bg-yellow-50',
        border: 'border-yellow-300',
        icon: 'text-yellow-600',
        title: 'text-yellow-900',
        text: 'text-yellow-800',
      }
    : {
        bg: 'bg-orange-50',
        border: 'border-orange-300',
        icon: 'text-orange-600',
        title: 'text-orange-900',
        text: 'text-orange-800',
      };

  const label = isMarginal ? 'Grenzfall' : 'Zu kalt';
  const ariaLabel = isMarginal
    ? 'Winterhärte: Grenzfall — in harten Wintern Frostschäden möglich'
    : 'Winterhärte: Nicht winterhart in deiner Zone';

  if (variant === 'compact') {
    const baseTooltip = isMarginal
      ? 'Grenzfall — in harten Wintern Frostschäden möglich'
      : 'Zu kalt für deine Zone — Kübelhaltung empfohlen';
    return (
      <div
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${colors.bg} border ${colors.border} ${colors.text}`}
        title={`${baseTooltip}. ${HARDINESS_DISCLAIMER}`}
        role="status"
        aria-label={ariaLabel}
      >
        <Snowflake className={`w-3 h-3 flex-shrink-0 ${colors.icon}`} aria-hidden="true" />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border ${colors.bg} ${colors.border}`}
      role="status"
      aria-label={ariaLabel}
    >
      <Snowflake
        className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colors.icon}`}
        aria-hidden="true"
      />
      <div>
        <div className={`text-sm font-bold mb-1 ${colors.title}`}>
          {isMarginal ? 'Grenzfall für deine Zone' : 'Nicht winterhart in deiner Zone'}
        </div>
        <p className={`text-sm leading-relaxed ${colors.text}`}>
          {isMarginal
            ? 'Diese Pflanze ist gerade noch winterhart in deiner Zone. In besonders harten Wintern kann es zu Frostschäden kommen.'
            : 'Diese Pflanze übersteht den Winter in deiner Zone ohne Schutz nicht. Kübelhaltung mit Einwinterung ist eine gute Alternative.'}{' '}
          {HARDINESS_DISCLAIMER}
        </p>
      </div>
    </div>
  );
};

export default HardinessWarningBadge;
